import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Grid, observeResizeEvent, Table, tableCellClasses } from '@kc-console/common-ui';
import { useContextMenuPreferenceStore } from '@kc-console/context';
import { np } from '@kc-console/core';

import { isElementWrappedIn } from '../../utils';
import {
  ActionBar,
  ActionBarSettingColumnSelect,
  ActionBarSettingsPageSizeSelect,
} from '../ActionBar';
import ActionBarSettingsCompactModeSwitch from '../ActionBar/ActionBarSettingsCompactModeSwitch';
import ActionBarSettingsResetButton from '../ActionBar/ActionBarSettingsResetButton';
import ActionBarSettingsTextEllipsisSelect from '../ActionBar/ActionBarSettingsTextEllipsisSelect';
import { DEFAULT_PAGE_SIZE } from './constants';
import DataTableTableContents from './DataTableTableContents';
import useColumnResize from './hooks/useColumnResize';
import useDataTableAutoResetHandlers from './useDataTableAutoResetHandlers';
import useDataTableColumns from './useDataTableColumns';
import {
  createBuiltinColumnVariableKeys,
  createColumnVariableKeys,
  dataTableClasses,
} from './utils';

import type { ChangeEvent, MouseEvent } from 'react';
import type { RowData, RowSelectionState, Updater, VisibilityState } from '@tanstack/react-table';
import type { CheckedState } from '@kc-console/common-ui';
import type { DataTableProps, FilterState } from './types';

export default function DataTable<TData extends RowData>(props: DataTableProps<TData>) {
  const {
    actions,
    autoReset,
    className,
    columnVirtualization,
    disabledExpandTooltip,
    disabledRowTooltip,
    emptyState,
    filter: filterProp,
    showActionBar = true,
    showExpandAllButton = true,
    showFilter = true,
    showPagination = true,
    showSetting = true,
    showAutoPadRightColumn = true,
    isError,
    error,
    errorState,
    isFetching,
    isDisconnected,
    isLoading,
    onRefetch,
    emptyStateProps,
    onRowClick,
    pagination: paginationProp,
    renderExpandedRow: renderExpandedRowProp,
    renderRowContextMenu,
    sorting,
    showSideBorder = false,
    showSideMargin = false,
    stickyHeader,
    style,
    table,
    tableRef,
    moreButtonLabel,
    refetchTooltip,
    visibleActionsCount,
    layoutType,
    ...others
  } = props;

  // Prepare options and configurations
  const { options } = table;
  const {
    //
    enableColumnResizing = true,
    enableRowExpanding,
    enableMultiRowSelection,
    enableRowSelection,
    enableExpanding,
    renderFallbackValue,
  } = options;

  const hasSelectionColumn = !!enableRowSelection;
  const hasSelectionColumnAsCheckbox = !!enableMultiRowSelection;
  const hasSelectionColumnAsRadio = !enableMultiRowSelection;
  const hasExpandColumn =
    enableRowExpanding === undefined ? !!enableExpanding : enableRowExpanding !== false;

  const tableRootRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableContainerWidth, setTableContainerWidth] = useState<number | null>(null);

  const columns = useDataTableColumns(table);
  const centerColumns = table.getCenterVisibleLeafColumns();
  const lastCenterColumn = centerColumns.at(-1);
  const hasAutoPadColumn =
    showAutoPadRightColumn && lastCenterColumn?.columnDef.meta?.column?.align === 'right';

  const headerSizeVariables = table.getHeaderGroups().reduce(
    (variables, headerGroup) => {
      headerGroup.headers.forEach((header, headerIndex, headers) => {
        const columnVariableKey = createColumnVariableKeys(header.column.id);
        const size = header.getSize();
        variables[columnVariableKey.size] = `${size}px`;
        const lastHeaderIndexInUnfixed = headers.findLastIndex(header =>
          header.getLeafHeaders().some(leaf => leaf.column.getIsPinned() === false),
        );
        if (lastHeaderIndexInUnfixed === headerIndex) {
          const totalWidth = headerGroup.headers.reduce(
            (acc, header) => acc + header.getSize(),
            (showSideMargin ? /* 양쪽 마진 */ 40 : 0) +
              (hasSelectionColumn ? /* 체크박스 컬럼 */ 48 : 0) +
              (hasExpandColumn ? /* 펼치기 컬럼 */ 48 : 0),
          );
          if (hasAutoPadColumn) {
            variables[createBuiltinColumnVariableKeys('autoPadRight').size] =
              tableContainerWidth && tableContainerWidth > totalWidth ? 'auto' : '0px';
          } else if (tableContainerWidth && tableContainerWidth > totalWidth) {
            variables[columnVariableKey.size] = `auto`;
          }
        }
        if (header.column.getIsResizing()) {
          variables[columnVariableKey.isResizing] = '1px';
        }

        const pinned = header.column.getIsPinned();
        if (pinned) {
          if (pinned === 'left') {
            const prevHeader = headers[headerIndex - 1];
            const firstHeaderOffset = hasSelectionColumn
              ? `calc(var(${createBuiltinColumnVariableKeys('selection').size}) + var(${createBuiltinColumnVariableKeys('selection').offset}))`
              : hasExpandColumn
                ? `calc(var(${createBuiltinColumnVariableKeys('expand').size}) + var(${createBuiltinColumnVariableKeys('expand').offset}))`
                : `0px`;
            variables[columnVariableKey.offset] = prevHeader
              ? `calc(var(${createColumnVariableKeys(prevHeader.column.id).size}) + var(${createColumnVariableKeys(prevHeader.column.id).offset}) + ${!!prevHeader && !headers[headerIndex - 2] ? (!hasExpandColumn && !hasSelectionColumn && showSideMargin ? 20 : 0) : 0}px)`
              : firstHeaderOffset;
          }
          if (pinned === 'right') {
            const nextHeader = headers[headerIndex + 1];
            variables[columnVariableKey.offset] = nextHeader
              ? `calc(var(${createColumnVariableKeys(nextHeader.column.id).size}) + var(${createColumnVariableKeys(nextHeader.column.id).offset}) + ${!!nextHeader && !headers[headerIndex + 2] ? (showSideMargin ? 20 : 0) : 0}px)`
              : `0px`;
          }
        }
      });

      return variables;
    },
    {
      [createBuiltinColumnVariableKeys('expand').size]: hasExpandColumn
        ? `${48 + (showSideMargin ? 20 : 0)}px`
        : `0px`,
      [createBuiltinColumnVariableKeys('expand').offset]: `0px`,
      [createBuiltinColumnVariableKeys('selection').size]: hasSelectionColumn
        ? `${48 + (!hasExpandColumn && showSideMargin ? 20 : 0)}px`
        : `0px`,
      [createBuiltinColumnVariableKeys('selection').offset]:
        `var(${createBuiltinColumnVariableKeys('expand').size})`,
      [createBuiltinColumnVariableKeys('autoPadRight').size]: '0px',
    } as Record<string, string>,
  );

  const resetRowSelection = useCallback(() => {
    table.resetRowSelection();
  }, [table]);

  const resetRowExpanded = useCallback(() => {
    table.resetExpanded();
  }, [table]);

  const { resetRowExpandedIfNeeded, resetRowSelectionIfNeeded } = useDataTableAutoResetHandlers({
    autoReset,
    context: {
      resetRowExpanded,
      resetRowSelection,
    },
  });

  const handleSortDirectionChange = useCallback(
    (targetKey: string, direction: 'asc' | 'desc' | false) => {
      const nextSorting = [
        ...(sorting?.multiple
          ? (sorting?.sorting ?? []).filter(({ key }) => key !== targetKey)
          : []),
        ...(direction === false ? [] : [{ key: targetKey, direction }]),
      ];

      sorting?.onSortingChange?.(nextSorting);
      resetRowExpandedIfNeeded('sort');
      resetRowSelectionIfNeeded('sort');
    },
    [
      sorting?.multiple,
      sorting?.sorting,
      sorting?.onSortingChange,
      resetRowExpandedIfNeeded,
      resetRowSelectionIfNeeded,
    ],
  );

  const { handleColumnResizeEvent } = useColumnResize({
    hasAutoPadColumn,
    hasExpandColumn,
    hasSelectionColumn,
    showSideMargin,
    table,
    tableContainerRef,
    tableContainerWidth,
    tableRootRef,
  });

  const handleColumnSizeReset = useCallback(
    (id: string) => {
      const column = table.getColumn(id);
      if (column) column.resetSize();
    },
    [table],
  );

  const getRowById = useCallback((rowId: string) => table.getRowModel().rowsById[rowId], [table]);

  const handleColumnOrderChange = useCallback(
    (columnOrder: string[]) => {
      table.setColumnOrder(columnOrder);
    },
    [table],
  );

  const handleColumnVisibilityChange = useCallback(
    (columnVisibility: VisibilityState) => {
      table.setColumnVisibility(columnVisibility);
    },
    [table],
  );

  const handleHeaderSelectionChange = useCallback(
    (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      table.toggleAllRowsSelected(checked);
    },
    [table],
  );

  const handleRowSelectionChange = useCallback(
    (updater: Updater<RowSelectionState>) => {
      table.setRowSelection(updater);
    },
    [table],
  );

  const handleRowSelectionToggle = useCallback(
    (rowId: string) => {
      handleRowSelectionChange(prev => {
        const next = { ...prev };
        if (prev[rowId]) {
          delete next[rowId];
        } else {
          next[rowId] = true;
        }
        return next;
      });
    },
    [handleRowSelectionChange],
  );

  const handleExpandHeaderChange = useCallback(
    (event: MouseEvent<HTMLButtonElement>, checked: boolean) => {
      event.stopPropagation();
      table.toggleAllRowsExpanded(checked);
    },
    [table],
  );

  const handleRowClick = useCallback(
    (event: MouseEvent<HTMLTableRowElement>, rowId: string) => {
      const row = getRowById(rowId);
      if (!row) return;

      onRowClick?.(event, row.original);

      if (hasSelectionColumn && row.getCanSelect() && !event.defaultPrevented) {
        const prev = table.getState().rowSelection;
        if (Object.entries(prev).length !== 1 || prev[rowId] !== true) {
          handleRowSelectionChange({ [rowId]: true });
        }
      }
    },
    [onRowClick, hasSelectionColumn, table, handleRowSelectionChange, getRowById],
  );

  const handleRowExpandingToggle = useCallback(
    (rowId: string, expanded?: boolean) => {
      const row = getRowById(rowId);
      if (!row) return;
      row.toggleExpanded(expanded);
    },
    [getRowById],
  );

  const renderExpandedRow = useMemo(() => {
    if (!renderExpandedRowProp) return undefined;

    return (params: { open: boolean; rowId: string }) => {
      const { open, rowId } = params;
      const row = getRowById(rowId);
      if (!row) return null;

      return renderExpandedRowProp({
        open,
        onClose: () => handleRowExpandingToggle(rowId, false),
        rowData: row.original,
      });
    };
  }, [renderExpandedRowProp, getRowById, handleRowExpandingToggle]);

  useEffect(() => {
    return observeResizeEvent(tableContainerRef, () => {
      setTableContainerWidth(tableContainerRef.current?.clientWidth ?? null);
    });
  }, []);

  const handleFiltersChange = useCallback(
    (filters: FilterState[]) => {
      filterProp?.onFiltersChange?.(filters);
      resetRowExpandedIfNeeded('filter');
      resetRowSelectionIfNeeded('filter');
    },
    [filterProp?.onFiltersChange, resetRowExpandedIfNeeded, resetRowSelectionIfNeeded],
  );

  const filter = filterProp && {
    ...filterProp,
    onFiltersChange: handleFiltersChange,
  };

  const handlePageIndexChange = useCallback(
    (pageIndex: number) => {
      paginationProp?.onPageIndexChange?.(pageIndex);
      resetRowExpandedIfNeeded('pageIndex');
      resetRowSelectionIfNeeded('pageIndex');
    },
    [paginationProp?.onPageIndexChange, resetRowExpandedIfNeeded, resetRowSelectionIfNeeded],
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      paginationProp?.onPageSizeChange?.(pageSize);
      resetRowExpandedIfNeeded('pageSize');
      resetRowSelectionIfNeeded('pageSize');
    },
    [paginationProp?.onPageSizeChange, resetRowExpandedIfNeeded, resetRowSelectionIfNeeded],
  );

  const pagination = useMemo(
    () =>
      paginationProp && {
        ...paginationProp,
        onPageIndexChange: handlePageIndexChange,
      },
    [paginationProp, handlePageIndexChange],
  );

  const settings = useMemo(
    () => ({
      columns,
      onColumnOrderChange: handleColumnOrderChange,
      onColumnVisibilityChange: handleColumnVisibilityChange,
    }),
    [columns, handleColumnOrderChange, handleColumnVisibilityChange],
  );

  useEffect(() => {
    if (isLoading) return;
    if (!pagination?.onPageIndexChange) return;

    const totalCount = pagination?.totalCount ?? 0;
    const pageSize = pagination?.pageSize ?? pagination?.defaultPageSize ?? DEFAULT_PAGE_SIZE;

    const pageCount = Math.ceil(totalCount / pageSize);
    const safePageCount = Math.max(1, pageCount);
    const pageIndex = pagination?.pageIndex ?? 1;

    if (pageIndex > safePageCount) {
      pagination.onPageIndexChange(safePageCount);
    }
  }, [isLoading, pagination]);

  const {
    showColumnSelect = true,
    showCompactModeSwitch = true,
    showPageSizeSelect = true,
    showTextEllipsisSelect = true,
    showViewTypeSelect: _showViewTypeSelect = true,
  } = typeof showSetting !== 'boolean' ? showSetting : {};

  const globalContextMenuEnabled = useContextMenuPreferenceStore(state => state.enabled);

  const isSettingsChanged =
    columns.some(col => col.defaultIsVisible !== col.isVisible) ||
    columns.some(col => col.orderIndex !== -1) ||
    columns.some(col => col.isColumnSizeChanged) ||
    (pagination && pagination.pageSize !== (pagination.defaultPageSize ?? DEFAULT_PAGE_SIZE)) ||
    table.getState().textEllipsis !== table.initialState.textEllipsis ||
    table.getState().compactMode !== table.initialState.compactMode;

  const settingContent = [
    showColumnSelect && (
      <ActionBarSettingColumnSelect
        key="column-select"
        columns={columns}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnVisibilityChange={handleColumnVisibilityChange}
      />
    ),
    showPageSizeSelect && (
      <ActionBarSettingsPageSizeSelect
        key="page-size-select"
        pageSize={pagination?.pageSize ?? pagination?.defaultPageSize ?? DEFAULT_PAGE_SIZE}
        pageSizeOptions={pagination?.pageSizeOptions}
        onPageSizeChange={handlePageSizeChange}
      />
    ),
    showTextEllipsisSelect && (
      <ActionBarSettingsTextEllipsisSelect
        key="text-ellipsis-select"
        textEllipsis={table.getState().textEllipsis}
        onTextEllipsisChange={table.setTextEllipsis}
      />
    ),
    showCompactModeSwitch && (
      <ActionBarSettingsCompactModeSwitch
        key="compact-mode-switch"
        compactMode={table.getState().compactMode}
        onCompactModeChange={table.setCompactMode}
      />
    ),
    isSettingsChanged && (
      <ActionBarSettingsResetButton
        key="reset-button"
        onReset={() => {
          table.resetColumnSizing();
          table.resetColumnVisibility();
          table.resetColumnOrder();
          pagination?.onPageSizeChange?.(pagination?.defaultPageSize ?? DEFAULT_PAGE_SIZE);
          table.resetTextEllipsis();
          table.resetCompactMode();
        }}
      />
    ),
  ];

  const [contextMenu, setContextMenu] = useState<{
    position: { left: number; top: number };
    rowId: string;
  } | null>(null);
  const hasRenderRowContextMenu = !!renderRowContextMenu;
  const handleRowContextMenu = useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>, rowId: string) => {
      if (
        e.target instanceof Element &&
        (isElementWrappedIn(e.target, HTMLAnchorElement) ||
          isElementWrappedIn(e.target, HTMLButtonElement))
      ) {
        return;
      }
      if (!hasRenderRowContextMenu) return;

      e.preventDefault();
      const row = getRowById(rowId);
      if (!row) return;
      setContextMenu({
        position: { left: e.clientX, top: e.clientY },
        rowId,
      });
    },
    [getRowById, hasRenderRowContextMenu],
  );

  const tableLayout = enableColumnResizing ? 'fixed' : 'auto';
  const selectionHeaderCheckedState: CheckedState = table.getIsAllPageRowsSelected()
    ? 'checked'
    : table.getIsSomePageRowsSelected()
      ? 'indeterminate'
      : 'unchecked';

  // 테이블을 그릴 때 필요한 테이블 자체와 스크롤 영역 정보
  const tableContext = {
    columnVirtualization,
    table,
    tableContainerRef,
    tableContainerWidth,
  };

  // 화면에 보여 줄 로딩·오류·빈 상태와 안내 문구 정보
  const displayState = {
    disabledExpandTooltip,
    disabledRowTooltip,
    emptyState,
    emptyStateProps,
    error,
    errorState,
    hasFilters: (filterProp?.filters ?? []).length > 0,
    isError,
    isLoading,
    layoutType,
    renderFallbackValue,
    showSideBorder,
  };

  // 선택·확장·정렬처럼 테이블에서 켜거나 끄는 기능 설정 값
  const featureState = {
    hasAutoPadColumn,
    hasExpandColumn,
    hasSelectionColumn,
    hasSelectionColumnAsCheckbox,
    hasSelectionColumnAsRadio,
    selectionHeaderCheckedState,
    showExpandAllButton,
    sorting: sorting?.sorting ?? [],
  };

  // 사용자가 클릭·선택·정렬할 때 실행할 함수 정보
  const interactions = {
    contextMenuEnabled: globalContextMenuEnabled,
    onColumnResize: handleColumnResizeEvent,
    onColumnSizeReset: handleColumnSizeReset,
    onExpandHeaderChange: handleExpandHeaderChange,
    onRefetch,
    onRowClick: handleRowClick,
    onRowContextMenu: handleRowContextMenu,
    onRowExpandingToggle: handleRowExpandingToggle,
    onRowSelectionChange: handleRowSelectionChange,
    onRowSelectionToggle: handleRowSelectionToggle,
    onSelectionHeaderChange: handleHeaderSelectionChange,
    onSortDirectionChange: handleSortDirectionChange,
    renderExpandedRow,
  };

  return (
    <DataTableRoot
      ref={tableRootRef}
      className={clsx(dataTableClasses.root, className)}
      showSideBorder={!!showSideBorder}
      container
      direction="column"
      flexWrap="nowrap"
      style={{
        ...style,
        ...headerSizeVariables,
      }}
      {...others}
    >
      {showActionBar && (
        <ActionBar
          actions={actions}
          className={dataTableClasses.actionBar}
          filter={filter}
          isFetching={isFetching}
          isDisconnected={isDisconnected}
          onRefetch={onRefetch}
          refetchTooltip={refetchTooltip}
          pagination={pagination}
          settings={settings}
          settingsContent={settingContent}
          isSettingsChanged={isSettingsChanged}
          showFilter={showFilter}
          showPagination={showPagination}
          showSetting={!!showSetting}
          showSideMargin={showSideMargin}
          moreButtonLabel={moreButtonLabel}
          visibleActionsCount={visibleActionsCount}
        />
      )}

      <DataTableTable
        size={table.getState().compactMode ? 'small' : 'medium'}
        className={dataTableClasses.table}
        ref={tableRef}
        maxHeight={stickyHeader ? '100%' : undefined}
        stickyHeader={stickyHeader}
        showSideMargin={showSideMargin}
        tableContainerProps={{
          ref: tableContainerRef,
          className: dataTableClasses.tableContainer,
        }}
        style={{ tableLayout: tableLayout }}
      >
        <DataTableTableContents
          displayState={displayState}
          featureState={featureState}
          interactions={interactions}
          tableContext={tableContext}
        />

        {contextMenu &&
          renderRowContextMenu &&
          getRowById(contextMenu.rowId) &&
          renderRowContextMenu({
            anchorPosition: contextMenu.position,
            onClose: () => setContextMenu(null),
            open: true,
            rowData: getRowById(contextMenu.rowId).original,
          })}
      </DataTableTable>
    </DataTableRoot>
  );
}

const DataTableRoot = styled(Grid, { shouldForwardProp: np('showSideBorder') })<{
  showSideBorder: boolean;
}>`
  max-height: 100%;
  position: relative;
  z-index: ${({ theme }) => theme.zIndex.base};

  ${({ theme, showSideBorder }) =>
    showSideBorder &&
    css`
      border-left: ${theme.mixins.border('border/weak/default')};
      border-right: ${theme.mixins.border('border/weak/default')};
    `}
`;

const DataTableTable = styled(Table)`
  .${dataTableClasses.disabledRow} .${tableCellClasses.root} {
    opacity: 0.3;
  }
`;
